const DB_NAME = "office-viewer-local";
const DB_VERSION = 1;
const FILE_STORE = "files";
const SHARED_STORE = "sharedBatches";

export type StoredFileSource = "local" | "handler" | "share" | "history";

export interface StoredFileRecord {
  id: string;
  name: string;
  type: string;
  size: number;
  lastModified: number;
  openedAt: number;
  source: StoredFileSource;
  blob: Blob;
}

export interface SharedFileRecord {
  id: string;
  name: string;
  type: string;
  size: number;
  lastModified: number;
  blob: Blob;
}

export interface SharedBatch {
  batchId: string;
  createdAt: number;
  title?: string;
  text?: string;
  url?: string;
  files: SharedFileRecord[];
}

export async function saveFiles(files: File[], source: StoredFileSource = "local") {
  const db = await openOfficeDb();
  const records = files.map((file) => toStoredRecord(file, source));

  await transactionDone(db, FILE_STORE, "readwrite", (store) => {
    for (const record of records) {
      store.put(record);
    }
  });

  db.close();
  return records;
}

export async function saveSharedFiles(files: SharedFileRecord[]) {
  const db = await openOfficeDb();
  const records = files.map((file) => ({
    id: crypto.randomUUID(),
    name: file.name,
    type: file.type || "application/octet-stream",
    size: file.size,
    lastModified: file.lastModified || Date.now(),
    openedAt: Date.now(),
    source: "share" as const,
    blob: file.blob
  }));

  await transactionDone(db, FILE_STORE, "readwrite", (store) => {
    for (const record of records) {
      store.put(record);
    }
  });

  db.close();
  return records;
}

export async function listStoredFiles() {
  const db = await openOfficeDb();
  const records = await requestToPromise<StoredFileRecord[]>(
    db.transaction(FILE_STORE, "readonly").objectStore(FILE_STORE).getAll()
  );
  db.close();
  return records.sort((a, b) => b.openedAt - a.openedAt);
}

export async function getStoredFile(id: string) {
  const db = await openOfficeDb();
  const record = await requestToPromise<StoredFileRecord | undefined>(
    db.transaction(FILE_STORE, "readonly").objectStore(FILE_STORE).get(id)
  );
  db.close();
  return record;
}

export async function touchStoredFile(record: StoredFileRecord) {
  const updated = { ...record, openedAt: Date.now(), source: "history" as const };
  const db = await openOfficeDb();
  await transactionDone(db, FILE_STORE, "readwrite", (store) => {
    store.put(updated);
  });
  db.close();
  return updated;
}

export async function deleteStoredFile(id: string) {
  const db = await openOfficeDb();
  await transactionDone(db, FILE_STORE, "readwrite", (store) => {
    store.delete(id);
  });
  db.close();
}

export async function clearStoredFiles() {
  const db = await openOfficeDb();
  await transactionDone(db, FILE_STORE, "readwrite", (store) => {
    store.clear();
  });
  db.close();
}

export async function consumeSharedBatch(batchId: string) {
  const db = await openOfficeDb();
  const transaction = db.transaction(SHARED_STORE, "readwrite");
  const store = transaction.objectStore(SHARED_STORE);
  const batch = await requestToPromise<SharedBatch | undefined>(store.get(batchId));
  if (batch) {
    store.delete(batchId);
  }
  await new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  db.close();
  return batch;
}

export function fileFromRecord(record: StoredFileRecord) {
  return new File([record.blob], record.name, {
    type: record.type,
    lastModified: record.lastModified
  });
}

function toStoredRecord(file: File, source: StoredFileSource): StoredFileRecord {
  return {
    id: crypto.randomUUID(),
    name: file.name || "untitled",
    type: file.type || "application/octet-stream",
    size: file.size,
    lastModified: file.lastModified || Date.now(),
    openedAt: Date.now(),
    source,
    blob: file
  };
}

function openOfficeDb() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(FILE_STORE)) {
        db.createObjectStore(FILE_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(SHARED_STORE)) {
        db.createObjectStore(SHARED_STORE, { keyPath: "batchId" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => void
) {
  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    action(transaction.objectStore(storeName));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
}

function requestToPromise<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
